"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import LogoMark from "@/components/virtualspace/logo-mark";
import { cn } from "@/lib/utils";

/** A quiet icon until a visitor asks about the platform behind their viewer. */
export function VirtualSpaceAttribution({
  className,
  opacity,
  size = 16,
  side = "top",
  align = "center",
}: {
  className?: string;
  opacity?: number;
  size?: 16 | 20;
  side?: "top" | "bottom";
  align?: "center" | "end";
}) {
  const [open, setOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<Element>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const invisible = opacity === 0;

  useEffect(() => {
    if (!open) return;
    let blurTimer: ReturnType<typeof setTimeout> | undefined;
    // Pointer events inside a vendor iframe do not reach Radix's outside handler.
    const onBlur = () => {
      blurTimer = setTimeout(() => {
        if (document.activeElement instanceof HTMLIFrameElement) setOpen(false);
      }, 0);
    };
    const onFullscreenChange = () => setOpen(false);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      clearTimeout(blurTimer);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [open]);

  return (
    <Popover open={open} onOpenChange={(next) => {
      if (next) {
        const fullscreen = document.fullscreenElement;
        setPortalContainer(fullscreen?.contains(triggerRef.current) ? fullscreen : undefined);
      }
      setOpen(next);
    }}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label="Powered by VirtualSpace"
          title="Powered by VirtualSpace"
          aria-hidden={invisible || undefined}
          disabled={invisible}
          tabIndex={invisible ? -1 : undefined}
          style={{ opacity }}
          className={cn("pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current disabled:pointer-events-none", className)}
        >
          <LogoMark layout="icon" size={size} variant={opacity === undefined && size === 16 ? "subtle" : "default"} alt="" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        portalContainer={portalContainer}
        aria-label="About VirtualSpace"
        side={side}
        align={align}
        sideOffset={8}
        collisionPadding={8}
        className="pointer-events-auto z-[1100] w-60 p-0 text-[#fff] [--glass-popover:rgb(10_10_10/0.98)] [--glass-edge:rgb(255_255_255/0.15)] [--card:0_0%_4%] motion-reduce:animate-none"
        onCloseAutoFocus={(event) => {
          // Returning to a viewer must not have its focus stolen by the icon.
          if (document.activeElement instanceof HTMLIFrameElement) event.preventDefault();
        }}
      >
        <a href="https://virtualspace.xyz" target="_blank" rel="noopener noreferrer" aria-label="Visit VirtualSpace website" className="block rounded-[inherit] p-4 transition-colors hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white">
          <span className="block text-[10px] uppercase tracking-[0.16em] text-[#999]">Powered by</span>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/brand/vs-wordmark-on-dark.png" alt="VirtualSpace" width={134} height={20} className="mt-2 h-5 w-auto" />
          <span className="mt-3 flex items-center gap-1 text-xs text-[#bfbfbf]">Visit website <ArrowUpRight className="size-3.5" aria-hidden /></span>
        </a>
      </PopoverContent>
    </Popover>
  );
}
